// ============================================
// Countdown Timer - Live auction countdown
// ============================================

import { useState, useEffect, useMemo } from "react";
import { UrgencyLevel } from "../../types";

interface CountdownTimerProps {
  endTime: string | Date;
  onExpire?: () => void;
  showLabels?: boolean;
  compact?: boolean;
  className?: string;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
  urgencyLevel: UrgencyLevel;
}

/**
 * Calculate time remaining and urgency level
 */
function calculateTimeRemaining(endTime: Date): TimeRemaining {
  const now = new Date();
  const diff = endTime.getTime() - now.getTime();

  if (diff <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      totalSeconds: 0,
      urgencyLevel: "ended",
    };
  }

  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // Determine urgency level
  let urgencyLevel: UrgencyLevel = "normal";
  if (totalSeconds <= 300) {
    // < 5 minutes - critical (flashing)
    urgencyLevel = "critical";
  } else if (totalSeconds <= 900) {
    // < 15 minutes - red
    urgencyLevel = "critical";
  } else if (totalSeconds <= 3600) {
    // < 1 hour - warning (yellow)
    urgencyLevel = "warning";
  }

  return {
    days,
    hours,
    minutes,
    seconds,
    totalSeconds,
    urgencyLevel,
  };
}

/**
 * Get CSS classes based on urgency level
 */
function getUrgencyStyles(urgencyLevel: UrgencyLevel, isFlashing: boolean): string {
  switch (urgencyLevel) {
    case "ended":
      return "text-green-800 border-green-800";
    case "critical":
      return isFlashing
        ? "text-red-500 border-red-500 animate-pulse bg-red-500/10"
        : "text-red-400 border-red-500";
    case "warning":
      return "text-yellow-400 border-yellow-500";
    case "normal":
    default:
      return "text-green-400 border-green-500";
  }
}

/**
 * Pad number with leading zero
 */
function pad(num: number): string {
  return num.toString().padStart(2, "0");
}

/**
 * CountdownTimer Component
 *
 * Live countdown timer with color-coded urgency levels:
 * - Green: > 1 hour remaining
 * - Yellow: 15min - 1 hour remaining
 * - Red: < 15 minutes remaining
 * - Flashing Red: < 5 minutes remaining
 */
export function CountdownTimer({
  endTime,
  onExpire,
  showLabels = false,
  compact = false,
  className = "",
}: CountdownTimerProps) {
  const endDate = useMemo(
    () => (typeof endTime === "string" ? new Date(endTime) : endTime),
    [endTime]
  );

  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(() =>
    calculateTimeRemaining(endDate)
  );
  const [isFlashing, setIsFlashing] = useState(false);

  // Update countdown every second
  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining(endDate);
      setTimeRemaining(remaining);

      if (remaining.totalSeconds <= 0 && onExpire) {
        onExpire();
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [endDate, onExpire]);

  // Flash effect for critical urgency (< 5 min)
  useEffect(() => {
    if (timeRemaining.urgencyLevel === "critical" && timeRemaining.totalSeconds <= 300) {
      const flashInterval = setInterval(() => {
        setIsFlashing((prev) => !prev);
      }, 500);
      return () => clearInterval(flashInterval);
    } else {
      setIsFlashing(false);
    }
  }, [timeRemaining.urgencyLevel, timeRemaining.totalSeconds]);

  const { days, hours, minutes, seconds, urgencyLevel, totalSeconds } = timeRemaining;
  const urgencyStyles = getUrgencyStyles(urgencyLevel, isFlashing);

  // Format display based on time remaining
  const formatTime = () => {
    if (urgencyLevel === "ended") {
      return "ENDED";
    }

    if (compact) {
      if (days > 0) {
        return `${days}D ${pad(hours)}H`;
      }
      if (hours > 0) {
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
      }
      return `${pad(minutes)}:${pad(seconds)}`;
    }

    if (showLabels) {
      if (days > 0) {
        return `${days}D ${pad(hours)}H ${pad(minutes)}M`;
      }
      return `${pad(hours)}H ${pad(minutes)}M ${pad(seconds)}S`;
    }

    if (days > 0) {
      return `${days}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  return (
    <div
      className={`font-mono tabular-nums tracking-wider ${urgencyStyles} ${className}`}
      title={`Ends: ${endDate.toLocaleString()}`}
    >
      <span className={totalSeconds <= 300 && totalSeconds > 0 ? "animate-pulse" : ""}>
        {formatTime()}
      </span>
    </div>
  );
}

/**
 * Compact inline countdown for tables
 */
export function InlineCountdown({
  endTime,
  className = "",
}: {
  endTime: string | Date;
  className?: string;
}) {
  return <CountdownTimer endTime={endTime} compact className={`text-sm ${className}`} />;
}

/**
 * Large countdown display for auction details
 */
export function LargeCountdown({
  endTime,
  label = "TIME REMAINING",
  className = "",
}: {
  endTime: string | Date;
  label?: string;
  className?: string;
}) {
  const endDate = useMemo(
    () => (typeof endTime === "string" ? new Date(endTime) : endTime),
    [endTime]
  );

  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(() =>
    calculateTimeRemaining(endDate)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(endDate));
    }, 1000);
    return () => clearInterval(interval);
  }, [endDate]);

  const { days, hours, minutes, seconds, urgencyLevel } = timeRemaining;
  const urgencyStyles = getUrgencyStyles(urgencyLevel, false);

  return (
    <div className={`border-2 p-4 bg-black ${urgencyStyles} ${className}`}>
      <div className="text-xs text-green-600 mb-2 tracking-wider">{label}</div>
      <div className="flex items-center justify-center gap-2 text-2xl font-bold">
        {days > 0 && (
          <>
            <div className="flex flex-col items-center">
              <span className={urgencyStyles}>{pad(days)}</span>
              <span className="text-xs text-green-700">DAYS</span>
            </div>
            <span className={urgencyStyles}>:</span>
          </>
        )}
        <div className="flex flex-col items-center">
          <span className={urgencyStyles}>{pad(hours)}</span>
          <span className="text-xs text-green-700">HRS</span>
        </div>
        <span className={urgencyStyles}>:</span>
        <div className="flex flex-col items-center">
          <span className={urgencyStyles}>{pad(minutes)}</span>
          <span className="text-xs text-green-700">MIN</span>
        </div>
        <span className={urgencyStyles}>:</span>
        <div className="flex flex-col items-center">
          <span className={urgencyStyles}>{pad(seconds)}</span>
          <span className="text-xs text-green-700">SEC</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to get time remaining for an auction
 */
export function useCountdown(endTime: string | Date) {
  const endDate = useMemo(
    () => (typeof endTime === "string" ? new Date(endTime) : endTime),
    [endTime]
  );

  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(() =>
    calculateTimeRemaining(endDate)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(endDate));
    }, 1000);
    return () => clearInterval(interval);
  }, [endDate]);

  return timeRemaining;
}

export default CountdownTimer;
